// CSV input parsing for the new-supply calculation.
//
// The CSV template is documented in `examples/csv-template.md`. It uses
// three sections — [PARCEL], [PRICES], [ITEMS] — each introduced by a
// sentinel header line.
//
// Strategy:
//   1. Parse the CSV body into a generic section map.
//   2. Build a JSON-shaped object (`DemoNewSupplyFile`).
//   3. Run it through `validateDemoFile` so structural validation stays
//      in one place (the JSON validator is the single source of truth
//      for required fields, enum membership, and numeric ranges).
//
// Errors are accumulated into `ValidationMessage[]` with `field`
// paths prefixed by the originating CSV row (e.g. `[PARCEL].landArea`,
// `[ITEMS][2].gfa`).

import type { ValidationMessage } from "../types/validation.js";

import { validateDemoFile } from "./validate.js";
import type { DemoNewSupplyFile } from "./input.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ParseNewSupplyCsvResult {
  readonly value: DemoNewSupplyFile | null;
  readonly errors: ValidationMessage[];
}

export function parseNewSupplyCsv(raw: string): ParseNewSupplyCsvResult {
  const trimmed = raw.replace(/^﻿/, "");
  const sections = splitIntoSections(trimmed);
  const errors: ValidationMessage[] = [];

  if (!sections.has("PARCEL")) {
    errors.push({
      level: "error",
      field: "[PARCEL]",
      message: "缺少 [PARCEL] 段落。",
    });
  }
  if (!sections.has("PRICES")) {
    errors.push({
      level: "error",
      field: "[PRICES]",
      message: "缺少 [PRICES] 段落。",
    });
  }
  if (!sections.has("ITEMS")) {
    errors.push({
      level: "error",
      field: "[ITEMS]",
      message: "缺少 [ITEMS] 段落。",
    });
  }
  if (errors.length > 0) {
    return { value: null, errors };
  }

  const parcelRows = sections.get("PARCEL")!;
  const pricesRows = sections.get("PRICES")!;
  const itemsRows = sections.get("ITEMS")!;

  const parcel = kvRowsToObject(parcelRows, "[PARCEL]", errors, PARCEL_STRING_KEYS);
  const prices = kvRowsToObject(pricesRows, "[PRICES]", errors, PRICE_STRING_KEYS);
  const items = itemsRowsToArray(itemsRows, "[ITEMS]", errors);

  if (errors.length > 0 || !parcel || !prices || !items) {
    return { value: null, errors };
  }

  const jsonInput = {
    calculationType: "new-supply" as const,
    input: { parcel, prices, items },
  };
  const validated = validateDemoFile(jsonInput);
  if (validated.errors.length > 0 || validated.value === null) {
    return {
      value: null,
      errors: validated.errors,
    };
  }
  // Narrow: validateDemoFile returned a non-batch value (because we
  // passed a new-supply shape), so this assertion is safe.
  return { value: validated.value as DemoNewSupplyFile, errors: [] };
}

// ---------------------------------------------------------------------------
// Sectioning
// ---------------------------------------------------------------------------

type SectionName = "PARCEL" | "PRICES" | "ITEMS";

function splitIntoSections(raw: string): Map<SectionName, string[][]> {
  const out = new Map<SectionName, string[][]>();
  let current: SectionName | null = null;
  let buffer: string[][] = [];

  const flush = () => {
    if (current !== null && buffer.length > 0) {
      out.set(current, buffer);
    }
    buffer = [];
  };

  for (const line of raw.split(/\r?\n/)) {
    if (line.trim() === "") continue;
    const header = matchSectionHeader(line);
    if (header) {
      flush();
      current = header;
      buffer = [];
      continue;
    }
    if (current === null) continue;
    buffer.push(parseCsvLine(line));
  }
  flush();
  return out;
}

function matchSectionHeader(line: string): SectionName | null {
  // Section header is a single cell like "[PARCEL]" (possibly with
  // surrounding whitespace).
  const cells = parseCsvLine(line);
  if (cells.length !== 1) return null;
  const cell = cells[0].trim();
  if (cell === "[PARCEL]") return "PARCEL";
  if (cell === "[PRICES]") return "PRICES";
  if (cell === "[ITEMS]") return "ITEMS";
  return null;
}

// ---------------------------------------------------------------------------
// CSV line parsing
// ---------------------------------------------------------------------------

/**
 * Parse a single CSV line. Supports double-quoted fields with `""` as
 * the escape for a literal double-quote. Commas inside quoted fields
 * are not treated as separators. This is enough for our template; we
 * do not aim to be a full RFC 4180 implementation.
 */
export function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let i = 0;
  const len = line.length;
  while (i < len) {
    const ch = line[i];
    if (ch === ",") {
      out.push("");
      i++;
      continue;
    }
    if (ch === '"') {
      // Quoted field: read until matching close quote, honour `""`.
      let value = "";
      i++; // consume opening quote
      while (i < len) {
        const c = line[i];
        if (c === '"') {
          if (i + 1 < len && line[i + 1] === '"') {
            value += '"';
            i += 2;
            continue;
          }
          i++; // consume closing quote
          break;
        }
        value += c;
        i++;
      }
      out.push(value);
      // After a quoted field, skip the rest until the next comma or EOL.
      while (i < len && line[i] !== ",") i++;
      if (i < len) i++; // consume the comma
      continue;
    }
    // Unquoted field: read until comma.
    let value = "";
    while (i < len && line[i] !== ",") {
      value += line[i];
      i++;
    }
    out.push(value);
    if (i < len) i++; // consume the comma
  }
  return out.map((c) => c.trim());
}

// ---------------------------------------------------------------------------
// Section → domain object
// ---------------------------------------------------------------------------

function kvRowsToObject(
  rows: string[][],
  sectionPath: string,
  errors: ValidationMessage[],
  stringKeys: ReadonlySet<string> = new Set(),
): Record<string, unknown> | null {
  const obj: Record<string, unknown> = {};
  let ok = true;
  rows.forEach((row, idx) => {
    const path = `${sectionPath}[${idx}]`;
    if (row.length < 2 || row[0].trim() === "") {
      errors.push({
        level: "error",
        field: path,
        message: `${path} 必须是 key,value 形式（两列）。`,
      });
      ok = false;
      return;
    }
    const key = row[0].trim();
    const raw = row[1] ?? "";
    if (obj[key] !== undefined) {
      errors.push({
        level: "error",
        field: `${path}.${key}`,
        message: `${path}.${key} 重复定义。`,
      });
      ok = false;
      return;
    }
    obj[key] = coerceScalar(raw, `${path}.${key}`, errors, stringKeys.has(key));
  });
  return ok ? obj : null;
}

function itemsRowsToArray(
  rows: string[][],
  sectionPath: string,
  errors: ValidationMessage[],
): Record<string, unknown>[] | null {
  if (rows.length === 0) {
    errors.push({
      level: "error",
      field: sectionPath,
      message: `${sectionPath} 段不能为空（至少需要字段名表头）。`,
    });
    return null;
  }
  const header = rows[0].map((c) => c.trim());
  if (header.length === 0 || header[0] === "") {
    errors.push({
      level: "error",
      field: `${sectionPath}[0]`,
      message: `${sectionPath} 表头必须至少包含一个列名。`,
    });
    return null;
  }
  if (rows.length === 1) {
    return [];
  }
  const out: Record<string, unknown>[] = [];
  let ok = true;
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const obj: Record<string, unknown> = {};
    for (let c = 0; c < header.length; c++) {
      const key = header[c];
      const raw = row[c] ?? "";
      obj[key] = coerceScalar(raw, `${sectionPath}[${r}].${key}`, errors);
    }
    out.push(obj);
  }
  // We do not flip `ok` for individual row errors — every cell error
  // is already pushed into the message sink. The downstream validator
  // will reject malformed rows; here we return the partial array so
  // the validator sees the same shape as JSON input.
  void ok;
  return out;
}

/**
 * Convert a CSV cell string into the appropriate JS primitive. Empty
 * cells become undefined. Otherwise:
 *   - `forceString` true   → keep as string
 *   - `true` / `false`     → boolean
 *   - numeric strings      → number
 *   - everything else      → string
 *
 * When the cell fails to parse as a number we push an error and
 * fall back to the original string so the downstream validator can
 * report a meaningful message.
 */
function coerceScalar(
  raw: string,
  field: string,
  errors: ValidationMessage[],
  forceString: boolean = false,
): unknown {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (forceString) return trimmed;
  // Try numeric.
  if (/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return trimmed;
}

// Keys whose values must remain strings (e.g. 4-digit years, dates,
// URLs). Used by [PARCEL] (everything is text) and [PRICES] (the
// metadata fields are text; only the per-category prices are numeric).
const PARCEL_STRING_KEYS: ReadonlySet<string> = new Set([
  "projectName",
  "parcelId",
  "supplyMethod",
  "calculationType",
  "calculationDate",
  "mainLandUse",
  "projectType",
  "remark",
]);

const PRICE_STRING_KEYS: ReadonlySet<string> = new Set([
  "priceSource",
  "priceDate",
  "marketPriceSource",
  "marketPriceYear",
  "valuationDate",
  "effectiveDate",
  "standardParcelCode",
  "benchmarkZoneCode",
  "officialQueryUrl",
  "priceSourceRemark",
]);